'use client'

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import Link from 'next/link'
import { useState, useEffect, useCallback } from 'react'

import { Lang } from '@/i18n'

export const BANNER_KEY = 'bh_football_banner_dismissed'
export const BANNER_VALUE = 'ekskurzija-spanija-italija-2026'
const HERO_IMAGE = '/blog/ekskurzija-spanija-italija-2026-banner.jpg'
const BLOG_SLUG = 'ekskurzija-spanija-italija-2026'

const copy = {
  bs: {
    title: 'eSIM za ekskurziju 2026',
    body: (
      <>
        Ovo je <span className='font-semibold text-gray-800'>promocija prilagođena maturantima</span> - nije standardna ponuda. Cijene su veoma povoljne baš za maturante.
      </>
    ),
    prices: [
      { data: '5 GB', price: '12 KM' },
      { data: '10 GB', price: '15 KM' },
    ],
    share: (
      <>
        <span className='font-semibold'>Poznaješ nekog maturanta koji ide na ekskurziju?</span> Podijeli ovu ponudu s njima.
      </>
    ),
    readMore: 'Pogledaj ponudu →',
    close: 'Zatvori',
    imageAlt: 'eSIM za ekskurziju 2026 — eSIMFly',
  },
  en: {
    title: 'eSIM for the 2026 excursion',
    body: (
      <>
        This is a <span className='font-semibold text-gray-800'>promotion tailored for graduates</span> - not the standard offer. Prices are made for students.
      </>
    ),
    prices: [
      { data: '5 GB', price: '12 KM' },
      { data: '10 GB', price: '15 KM' },
    ],
    share: (
      <>
        <span className='font-semibold'>Know a graduate going on the trip?</span> Share this offer with them.
      </>
    ),
    readMore: 'See the offer →',
    close: 'Close',
    imageAlt: 'eSIM for the 2026 excursion — eSIMFly',
  },
}

interface AnnouncementBannerProps {
  lang: Lang
}

export default function AnnouncementBanner({ lang }: AnnouncementBannerProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // const timeout = setTimeout(() => {
    //   const dismissed = localStorage.getItem(BANNER_KEY)
    //   if (dismissed === BANNER_VALUE) return
    //   setOpen(true)
    // }, 800)
    // return () => clearTimeout(timeout)
  }, [])

  const dismiss = useCallback(() => {
    setOpen(false)
    localStorage.setItem(BANNER_KEY, BANNER_VALUE)
  }, [])

  const t = copy[lang] ?? copy.bs
  const blogHref = `/${lang}/blogs/${BLOG_SLUG}`

  return (
    <Dialog open={open} as='div' className='relative z-[10000] focus:outline-none' onClose={dismiss}>
      <DialogBackdrop transition className='fixed inset-0 bg-black/50 backdrop-blur-sm duration-300 ease-out data-[closed]:opacity-0' />

      <div className='fixed inset-0 flex items-end justify-center md:items-center md:p-6'>
        <DialogPanel
          transition
          className='
            flex w-full max-w-2xl max-h-[90dvh] flex-col overflow-hidden
            rounded-t-2xl bg-white shadow-2xl
            md:rounded-2xl
            duration-300 ease-out
            data-[closed]:translate-y-full
            md:data-[closed]:translate-y-0 md:data-[closed]:scale-95 md:data-[closed]:opacity-0
            pb-[env(safe-area-inset-bottom)] md:pb-0
          '
        >
          {/* <div className='flex shrink-0 flex-col border-b border-gray-100'>
            <div className='flex justify-center pt-3 pb-1 md:hidden' aria-hidden='true'>
              <span className='h-1.5 w-10 rounded-full bg-gray-300' />
            </div>
            <div className='flex items-start gap-3 px-4 pb-3 pt-1 md:px-5 md:pt-4 md:pb-4'>
              <DialogTitle as='h2' className='min-w-0 flex-1 text-base font-bold leading-snug text-gray-900 sm:text-lg'>
                {t.title}
              </DialogTitle>
              <button
                type='button'
                onClick={dismiss}
                aria-label={t.close}
                className='
                  -mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                  bg-gray-100 text-gray-700 hover:bg-gray-200
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-900
                '
              >
                <XMarkIcon className='h-6 w-6' />
              </button>
            </div>
          </div> */}

          <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
            <div className='relative w-full'>
              <Image src={HERO_IMAGE} alt={t.imageAlt} width={1024} height={426} className='h-auto w-full object-cover' priority />
            </div>

            <div className='px-5 py-5'>
              <p className='mb-4 text-center text-sm leading-relaxed text-gray-600'>{t.body}</p>

              <div className='mb-5 grid grid-cols-2 gap-3'>
                {t.prices.map(plan => (
                  <div key={plan.data} className='flex items-center justify-around gap-0 p-0 rounded-xl border border-blue-100 bg-blue-50'>
                    <p className='text-2xl font-bold text-blue-900'>{plan.data}</p>
                    <p className='text-2xl font-semibold text-blue-800'>{plan.price}</p>
                  </div>
                ))}
              </div>

              <div className='rounded-xl border border-amber-100 bg-amber-50 px-4 py-3.5'>
                <p className='text-sm leading-relaxed text-amber-800'>{t.share}</p>
              </div>
            </div>
          </div>

          <div className='shrink-0 border-t border-gray-100 bg-white px-5 py-4'>
            <Link
              href={blogHref}
              onClick={dismiss}
              className='inline-flex w-full items-center justify-center rounded-xl bg-blue-900 px-6 py-3.5 text-sm font-semibold text-white shadow-md shadow-blue-200 transition-all hover:opacity-90 active:scale-[0.98]'
            >
              {t.readMore}
            </Link>
            <button type='button' onClick={dismiss} className='mt-3 w-full py-1 text-sm text-gray-400 transition-colors hover:text-gray-600'>
              {t.close}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export function DismissAnnouncementOnMount() {
  useEffect(() => {
    localStorage.setItem(BANNER_KEY, BANNER_VALUE)
  }, [])

  return null
}
